(ns hai.web-app.routing
  "Frontend routing plumbing

  - (<e [:routing/navigate route-name]) — to navigate
  - (<s :routing/current-route) — current reitit route match object
  - (<s :routing/router) — reitit router
  - ::routing/window-title - Route metadata, can be either a raw string or a subscription that returns a string, which is set to the window title
  "
  (:require
   ["react" :refer [useEffect]]
   [clojure.string :as str]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent defevent-fx defsub defeffect]]
   [hai.web-app.util.rf-storage :as rf-storage]
   [lambdaisland.glogi :as log]
   [lambdaisland.uri :as uri]
   [reitit.core :as reitit]
   [reitit.frontend :as rf]
   [reitit.frontend.controllers :as rfc]
   [reitit.frontend.easy :as rfe]
   [hai.web-app.routing :as-alias routing]
   [reagent.core :as r]))

(def href rfe/href)

(def link-target
  "Target for links that should be a cheap client-side route change when this
   page is already the top-level document, but escape via a real navigation
   when embedded (e.g. the browser extension's new-tab iframe).

   \"_self\" is treated by reitit's global click interceptor the same as no
   target at all, so it still gets intercepted and routed client-side. A
   non-_self target (\"_top\") is ignored by that interceptor, so the browser's
   default anchor behavior runs instead, navigating the real top-level
   browsing context out of the iframe."
  (if (= js/window js/window.top) "_self" "_top"))

(defevent-fx :routing/refresh-page [_]
  {:routing/refresh-page true})

(defeffect :routing/refresh-page [_]
  (js/location.reload))

(defeffect :routing/push-state [route]
  (apply rfe/push-state route))

(defsub :routing/current-route [db]
  (:routing/current-route db))

(defsub :routing/current-route-data
  [current-route <- [:routing/current-route]]
  (get current-route :data))

(defsub :routing/current-route-name
  [route-data <- [:routing/current-route-data]]
  (get route-data :name))

(defsub :routing/path-params
  [current-route <- [:routing/current-route]]
  (:path-params current-route))

(defsub :routing/query-params
  [current-route <- [:routing/current-route]]
  (:query-params current-route))

(defevent-fx :routing/navigate [{:keys [db]} route]
  {:routing/push-state (if (vector? route) route [route])})

;; Navigate to a path string (e.g. a `?return-to=` param carried across a
;; detour). Resolved back to a route name so it goes through `push-state` like
;; every other navigation — a raw `history.pushState` wouldn't re-render, since
;; reitit only listens for popstate. An unmatched path is a no-op.
(defevent-fx :routing/navigate-to-path [{:keys [db]} path]
  (if-let [match (reitit/match-by-path (:routing/router db) path)]
    {:routing/push-state [(-> match :data :name)
                          (:path-params match)
                          (:query-params match)]}
    {}))

(defeffect :routing/navigate-in-new-tab [url]
  (js/window.open url "_blank" "noopener,noreferrer"))

(defeffect :routing/navigate-to-url [url]
  (set! js/window.location.href url))

(defevent-fx :routing/navigate-in-new-tab [{:keys [db]} route]
  {:routing/navigate-in-new-tab (cond
                                  (string? route) route
                                  (vector? route) (apply href route)
                                  :else (href route))})

(defevent :routing/on-navigate [db new-match]
  (let [old-match (:routing/current-route db)]
    (assoc db
           :routing/current-route
           (assoc new-match
                  :controllers (rfc/apply-controllers (:controllers old-match) new-match)))))

(defevent :routing/register-router [db router]
  (assoc db :routing/router router))

(defsub :routing/route-details [db route-name & args]
  (apply reitit/match-by-name (:routing/router db) route-name args))

(defsub :route/data
  [db route-name & args]
  (-> (apply reitit/match-by-name (:routing/router db) route-name args)
      :data))

;; ── navbar open/closed state ────────────────────────────────────────────────
;; Drawer-style: collapsed shows a 64px icon rail; open expands to the full nav
;; and pushes main content right. Toggled via the hamburger in the navbar.
;; Persisted across reloads under [:settings/local :nav/navbar-open?].
;; Defaults to open so new desktop users see labelled nav items; once a user
;; toggles it, their stored preference takes over. Mobile uses its own shell
;; (hai.web-app.mobile) and is unaffected.

(defevent :nav/navbar-open
  <-> [(rf-storage/settings-local)]
  [db]
  (assoc-in db [:settings/local :nav/navbar-open?] true))

(defevent :nav/navbar-close
  <-> [(rf-storage/settings-local)]
  [db]
  (assoc-in db [:settings/local :nav/navbar-open?] false))

(defevent :nav/navbar-toggle
  <-> [(rf-storage/settings-local)]
  [db]
  (update-in db [:settings/local :nav/navbar-open?] not))

(defsub :nav/navbar-open? [db]
  (get-in db [:settings/local :nav/navbar-open?] true))

(defn- window-title-component*
  []
  (r/with-let [!current-route (<s :routing/current-route)]
    (let [{{window-title ::routing/window-title} :data} @!current-route
          window-title (or (if (keyword? window-title) @(<s window-title) window-title)
                           "Humble AI")]
      (useEffect (fn []
                   (set! js/document.title window-title)
                   (constantly nil))
                 #js [(str window-title)])
      [:<>])))

(defn window-title-component []
  [:f> window-title-component*])

(defn route-handler [new-match]
  (when new-match
    (>e [:routing/on-navigate new-match])))

(defn normalize-path
  "Remove trailing slash from path unless it's the root path"
  [path]
  (if (and (> (count path) 1) (str/ends-with? path "/"))
    (subs path 0 (dec (count path)))
    path))

(defn redirect-trailing-slash-middleware [router handler]
  (fn [new-match]
    (log/info :match new-match)
    (let [current-uri (uri/uri js/location)
          normalized-uri (update (uri/uri js/location) :path normalize-path)]
      (if (and (nil? new-match) (not= current-uri normalized-uri))
        ;; Try to redirect to normalized path (without trailing slash)
        (when-let [match (rf/match-by-path router normalized-uri)]
          (rfe/replace-state (-> match :data :name) (:path-params match) (:query-params match) (:fragment match)))
        ;; Process the route normally
        (handler new-match)))))

(defn init! [routes]
  (let [router (rf/router routes)]
    (>e! [:routing/register-router router])
    (rfe/start!
     router
     (redirect-trailing-slash-middleware router route-handler)
     {:use-fragment false})))

(comment
  @re-frame.db/app-db
  @(<s :routing/current-route)
  @(<s :routing/current-route-name)
  (>e [:routing/navigate [:flows/home]])
  (>e [:routing/navigate [:builder/debug-view]])
  (>e [:routing/navigate [:builder/workspaces-view]]))
